import React, { useState, useEffect } from 'react';
import { Building, Plus, Edit3, Trash2, Grid, ChevronUp, ChevronDown, Save, X, ArrowLeft } from 'lucide-react';
import timetableService from '../../services/timetableService';
import '../../styles/RoomSettings.css';

const RoomSettings = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [mode, setMode] = useState('Modify'); // Add, Modify, Delete, Cancel, Save
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    room_code: '',
    room_key: '',
    room_name: '',
    zone: '',
    location_x: '',
    location_y: '',
    capacity: 1,
    assignment_priority: 0,
    remark: ''
  });

  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      const data = await timetableService.getRooms();
      setRooms(data);
      if (data.length > 0 && !selectedRoom) {
        setSelectedRoom(data[0]);
        setFormData({
          room_code: data[0].room_code || '',
          room_key: data[0].room_key || '',
          room_name: data[0].room_name || '',
          zone: data[0].zone || '',
          location_x: data[0].location_x || '',
          location_y: data[0].location_y || '',
          capacity: data[0].capacity || 1,
          assignment_priority: data[0].assignment_priority || 0,
          remark: data[0].remark || ''
        });
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
    setFormData({
      room_code: room.room_code || '',
      room_key: room.room_key || '',
      room_name: room.room_name || '',
      zone: room.zone || '',
      location_x: room.location_x || '',
      location_y: room.location_y || '',
      capacity: room.capacity || 1,
      assignment_priority: room.assignment_priority || 0,
      remark: room.remark || ''
    });
    setMode('Modify');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdd = () => {
    setMode('Add');
    setFormData({
      room_code: '',
      room_key: '',
      room_name: '',
      zone: '',
      location_x: '',
      location_y: '',
      capacity: 1,
      assignment_priority: 0,
      remark: ''
    });
  };

  const handleModify = () => {
    setMode('Modify');
  };

  const handleDelete = async () => {
    if (selectedRoom && window.confirm('確定要刪除此課室嗎？')) {
      try {
        await timetableService.deleteRoom(selectedRoom.id);
        await loadRooms();
        setMode('Modify');
      } catch (error) {
        console.error('Error deleting room:', error);
      }
    }
  };

  const handleCancel = () => {
    if (selectedRoom) {
      setFormData({
        room_code: selectedRoom.room_code || '',
        room_key: selectedRoom.room_key || '',
        room_name: selectedRoom.room_name || '',
        zone: selectedRoom.zone || '',
        location_x: selectedRoom.location_x || '',
        location_y: selectedRoom.location_y || '',
        capacity: selectedRoom.capacity || 1,
        assignment_priority: selectedRoom.assignment_priority || 0,
        remark: selectedRoom.remark || ''
      });
    }
    setMode('Modify');
  };

  const handleSave = async () => {
    try {
      if (mode === 'Add') {
        await timetableService.createRoom(formData);
      } else if (mode === 'Modify' && selectedRoom) {
        await timetableService.updateRoom(selectedRoom.id, formData);
      }
      await loadRooms();
      setMode('Modify');
    } catch (error) {
      console.error('Error saving room:', error);
    }
  };

  const incrementCapacity = () => {
    setFormData(prev => ({ ...prev, capacity: prev.capacity + 1 }));
  };

  const decrementCapacity = () => {
    setFormData(prev => ({ ...prev, capacity: Math.max(1, prev.capacity - 1) }));
  };

  const incrementPriority = () => {
    setFormData(prev => ({ ...prev, assignment_priority: prev.assignment_priority + 1 }));
  };

  const decrementPriority = () => {
    setFormData(prev => ({ ...prev, assignment_priority: Math.max(0, prev.assignment_priority - 1) }));
  };
  if (loading) {
    return (
      <div className="room-settings-container">
        <div className="loading-container">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
            <p className="loading-text">載入課室資料中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="room-settings-container">
      {/* Header */}
      <div className="room-settings-header">
        <h2>Room Details (TBM0125E)</h2>
        <p className="subtitle">課室設定 - 管理課室資料和浮動課節編配設定</p>
      </div>

      {/* Main Content */}
      <div className="room-settings-content">
        {/* Room Form Panel */}
        <div className="room-form-panel">
          <h3 className="room-form-title">Room Details (TBM0125E)</h3>
          
          <div className="room-form-grid">
            {/* Room Code */}
            <div className="room-form-group">
              <label className="room-form-label">Room Code:</label>
              <input
                type="text"
                value={formData.room_code}
                onChange={(e) => handleInputChange('room_code', e.target.value)}
                className="room-form-input"
                placeholder="101"
              />
            </div>

            {/* Key */}
            <div className="room-form-group">
              <label className="room-form-label">Key:</label>
              <input
                type="text"
                value={formData.room_key}
                onChange={(e) => handleInputChange('room_key', e.target.value)}
                className="room-form-input"
                placeholder="101"
              />
            </div>

            {/* Room Name */}
            <div className="room-form-group">
              <label className="room-form-label">Room Name:</label>
              <input
                type="text"
                value={formData.room_name}
                onChange={(e) => handleInputChange('room_name', e.target.value)}
                className="room-form-input"
                placeholder="1A"
              />
            </div>

            {/* Zone */}
            <div className="room-form-group">
              <label className="room-form-label">Zone:</label>
              <div className="room-input-with-button">
                <input
                  type="text"
                  value={formData.zone}
                  onChange={(e) => handleInputChange('zone', e.target.value)}
                  className="room-form-input"
                  placeholder="01"
                />
                <button type="button" className="room-input-button">
                  <Grid size={16} />
                </button>
              </div>
            </div>

            {/* Location */}
            <div className="room-form-group full-width">
              <label className="room-form-label">Location:</label>
              <div className="room-location-inputs">
                <input
                  type="text"
                  value={formData.location_x}
                  onChange={(e) => handleInputChange('location_x', e.target.value)}
                  className="room-form-input"
                  placeholder=""
                />
                <span className="room-location-separator">&</span>
                <input
                  type="text"
                  value={formData.location_y}
                  onChange={(e) => handleInputChange('location_y', e.target.value)}
                  className="room-form-input"
                  placeholder=""
                />
              </div>
            </div>

            {/* Capacity */}
            <div className="room-form-group">
              <label className="room-form-label">Capacity:</label>
              <div className="room-number-input-group">
                <input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => handleInputChange('capacity', parseInt(e.target.value) || 1)}
                  className="room-form-input"
                  min="1"
                />
                <div className="room-number-controls">
                  <button type="button" onClick={incrementCapacity} className="room-number-btn">
                    <ChevronUp size={12} />
                  </button>
                  <button type="button" onClick={decrementCapacity} className="room-number-btn">
                    <ChevronDown size={12} />
                  </button>
                </div>
              </div>
            </div>

            {/* Assignment Priority */}
            <div className="room-form-group">
              <label className="room-form-label">Assignment Priority for Floating Lessons:</label>
              <div className="room-number-input-group">
                <input
                  type="number"
                  value={formData.assignment_priority}
                  onChange={(e) => handleInputChange('assignment_priority', parseInt(e.target.value) || 0)}
                  className="room-form-input"
                  min="0"
                />
                <div className="room-number-controls">
                  <button type="button" onClick={incrementPriority} className="room-number-btn">
                    <ChevronUp size={12} />
                  </button>
                  <button type="button" onClick={decrementPriority} className="room-number-btn">
                    <ChevronDown size={12} />
                  </button>
                </div>
              </div>
              <p className="room-help-text">(set to 0 to disable auto-assignment)</p>
            </div>

            {/* Remark */}
            <div className="room-form-group full-width">
              <label className="room-form-label">Remark:</label>
              <textarea
                value={formData.remark}
                onChange={(e) => handleInputChange('remark', e.target.value)}
                className="room-form-textarea"
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Action Buttons Panel */}
        <div className="action-buttons-panel">
          <button
            onClick={() => window.history.back()}
            className="action-button secondary"
          >
            <ArrowLeft size={16} />
            Prev Page
          </button>
          <button
            onClick={handleAdd}
            className="action-button success"
          >
            <Plus size={16} />
            Add
          </button>
          <button
            onClick={handleModify}
            className="action-button primary"
          >
            <Edit3 size={16} />
            Modify
          </button>
          <button
            onClick={handleDelete}
            className="action-button danger"
          >
            <Trash2 size={16} />
            Delete
          </button>
          <button
            onClick={handleCancel}
            className="action-button secondary"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="action-button success"
          >
            <Save size={16} />
            Save
          </button>
        </div>
      </div>

      {/* Room List Panel */}
      <div className="room-list-panel">
        <div className="room-list-header">
          <h3 className="room-list-title">Current Room List:</h3>
          <span className="room-list-count">{rooms.length}</span>
        </div>
        
        <div className="room-list">
          {rooms.map((room, index) => (
            <div
              key={room.id}
              onClick={() => handleRoomSelect(room)}
              className={`room-item ${selectedRoom?.id === room.id ? 'selected' : ''}`}
            >
              <div className="room-item-code">
                {room.room_code}-{room.room_key}-{room.room_name}-{room.zone}-&-{room.capacity}-{room.assignment_priority}...
              </div>
              <div className="room-item-details">
                Room Code: {room.room_code} | Room Name: {room.room_name} | Zone: {room.zone} | Capacity: {room.capacity}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mode Indicator */}
      <div className="mode-indicator">
        Mode: {mode}
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h4 className="instructions-title">課室設定指引</h4>
        <div className="instructions-content">
          <p><strong>1. 檢查課室資料和容量：</strong> 如果所需課室的「容納班數」為0，表示該課室不能用於教學。</p>
          <p><strong>2. 處理重複課室索引：</strong> 如果系統檢測到相同的「房間索引」值，會自動在後續重複的房間索引前加上「~」符號。</p>
          <p><strong>3. 重新命名課室索引：</strong> 建議將以「~」開頭的房間索引更改為更容易理解的名稱。</p>
          <p><strong>4. 設定課室位置用於浮動課節：</strong> 「位置」設定用於定義課室的物理位置，系統會使用兩條路徑來找到最接近的空置課室。</p>
          <p><strong>5. 浮動課節編配次序設定：</strong> 設定為「0」禁用自動編配，「1」優先編配，「2」可用於自動編配。</p>
          <p><strong>6. 儲存和導航：</strong> 完成輸入後點擊「儲存」按鈕記錄資料，點擊「前頁」返回主頁。</p>
        </div>
      </div>
    </div>
  );
};

export default RoomSettings;
